import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Form, Formik, FormikHelpers } from 'formik'
import { Button, FormControl, Grid, InputLabel, MenuItem, Select, Stack } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { Asset, AssetInput, Project } from '../types'
import { getProjects } from '../services/projectService'
import { updateAsset } from '../services/assetService'
import { createErrorNotification, createSuccessNotification } from '../redux/slices/notifications'

export interface AssetProjectFormProps {
  asset: Asset
  beforeSubmit?: (values: AssetInput) => void
  afterSubmit?: (asset: Asset) => void
  onCancel?: () => void
}

export default function AssetProjectForm({
  asset,
  beforeSubmit,
  afterSubmit,
  onCancel
}: AssetProjectFormProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('asset')

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The projects state.
   */
  const [projects, setProjects] = useState<Project[]>([])

  /**
   * The loading state.
   */
  const [loading, setLoading] = useState<boolean>(true)

  /**
   * The initial form values.
   */
  const initialValues: AssetInput = {
    name: asset.name,
    company: asset.companyId,
    project: asset.projectId,
    level: asset.levelId,
    lift: asset.liftId
  }

  /**
   * Load the projects.
   */
  async function loadProjects(): Promise<void> {
    try {
      setLoading(true)

      setProjects((await getProjects()).sort((a, b) => a.name.localeCompare(b.name)))
    } catch (_) {
      // Do nothing.
    } finally {
      setLoading(false)
    }
  }

  /**
   * Handle the "submit" event.
   */
  async function handleSubmit(values: AssetInput, { setSubmitting }: FormikHelpers<AssetInput>) {
    try {
      setSubmitting(true)

      if (asset.projectId !== values.project) {
        values.level = null
        values.lift = null
      }

      beforeSubmit?.(values)

      const updatedAsset = await updateAsset(asset.id, values)
      dispatch(createSuccessNotification(t('updateAssetSuccess')))

      setSubmitting(false)
      afterSubmit?.(updatedAsset)
    } catch (_) {
      dispatch(createErrorNotification(t('updateAssetError')))
      setSubmitting(false)
    }
  }

  /**
   * Load the projects when the component mounts.
   */
  useEffect(() => {
    loadProjects()
  }, [])

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit}>
      {({ values, setFieldValue, setFieldTouched, isSubmitting, handleChange, handleSubmit }) => (
        <Form onSubmit={handleSubmit}>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="project-select-label">{t('project')}</InputLabel>
                <Select
                  id="project-select"
                  labelId="project-select-label"
                  name="project"
                  label={t('project')}
                  value={values.project}
                  disabled={loading || isSubmitting}
                  autoFocus
                  onChange={handleChange}
                >
                  {projects.map((project) => (
                    <MenuItem key={project.id} value={project.id}>
                      {project.name}
                    </MenuItem>
                  ))}
                </Select>
                {values.project !== null && (
                  <Stack direction="row" justifyContent="flex-end" alignItems="center">
                    <Button
                      variant="text"
                      color="inherit"
                      size="small"
                      disabled={isSubmitting}
                      sx={{ mt: 1 }}
                      onClick={() => {
                        setFieldValue('project', null)
                        setFieldTouched('project', true)
                      }}
                    >
                      {t('clearSelection')}
                    </Button>
                  </Stack>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Stack direction="row" justifyContent="flex-end" spacing={2}>
                <Button variant="text" color="inherit" disabled={isSubmitting} onClick={onCancel}>
                  {t('cancel')}
                </Button>

                <LoadingButton
                  type="submit"
                  variant="contained"
                  color="primary"
                  loading={isSubmitting}
                >
                  {t('save')}
                </LoadingButton>
              </Stack>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  )
}
